import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import { CreateHiringDto, UpdateHiringDto } from './dto/hiring-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { HiringType } from '@/types/hiring.type'
import { ListResponse } from '@/types/list.type'

export class HiringService {
  private static instance: HiringService
  private readonly api: AxiosInstance

  constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): HiringService {
    if (!HiringService.instance) {
      HiringService.instance = new HiringService(baseService)
    }
    return HiringService.instance
  }

  getHirings = async (
    {
      queryParams,
    }: {
      queryParams: QueryParams
    },
    configs?: AxiosRequestConfig
  ) => {
    const { pageNumber, pageSize, sort, search, searchFields } = queryParams
    let url = '/hiring/'

    if (pageNumber && pageSize) {
      url += `?pageNumber=${pageNumber}&pageSize=${pageSize}`
    }
    if (sort) {
      url += `&sort=${sort}`
    }
    if (search) {
      url += `&search=${search}`
    }
    if (searchFields) {
      url += `&searchFields=${searchFields.join(',')}`
    }
    const { data } = await this.api.get<ListResponse<HiringType>>(url, configs)
    return data
  }

  getPublicHirings = async <T = ListResponse<HiringType>>({
    pageNumber,
    pageSize,
    sort = [],
  }: {
    pageNumber: number
    pageSize: number
    sort?: string[]
  }) => {
    let url = `/hiring/publish?pageNumber=${pageNumber}&pageSize=${pageSize}`
    if (sort.length > 0) {
      url += `&sort=${sort.join(',')}`
    }
    const { data } = await this.api.get<T>(url)
    return data
  }

  createHiring = async (hiringDto: CreateHiringDto) => {
    const { data } = await this.api.post(`/hiring/create`, hiringDto)
    return data
  }

  deleteHiring = async (id: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.delete(`/hiring/${id}`, options)
    return data
  }

  updateHiring = async (id: number, hiringDto: UpdateHiringDto) => {
    const { data } = await this.api.put(`/hiring/update/${id}`, hiringDto)
    return data
  }

  getAdminHiringById = async <T>(id: number, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/hiring/${id}`, options)
    return data
  }

  getHiringBySlug = async <T = HiringType>(slug: string, options?: AxiosRequestConfig) => {
    const { data } = await this.api.get<T>(`/hiring/slug/${slug}`, options)
    return data
  }
}

const hiringService = HiringService.getInstance(baseService)

export default hiringService
